import { createCaptions } from "../captionsElement";
import { createCCButton } from "../buttons";

// Centralized HUD state
import {
  applyCcState,
  readCcPref,
} from "../../utils/hudState";
import { getBaseUrl } from "../../utils/config";

export interface CaptionsHandle {
  mountIfNeeded: () => void;
  unmountIfPresent: () => void;
}

export function initCaptions(
  video: HTMLVideoElement,
  section: HTMLElement,
  controls: HTMLElement
): CaptionsHandle {
  const baseUrl = getBaseUrl().replace(/\/$/, "");
  let onCCChangeListener: ((e: Event) => void) | null = null;
  let captionsHandle: ReturnType<typeof createCaptions> | null = null;

  const removeCcButton = () => {
    const ccBtn = controls.querySelector(".cc-button") as HTMLButtonElement | null;
    if (!ccBtn) return;
    const stack = ccBtn.closest(".button-stack");
    if (stack) stack.remove();
    else ccBtn.remove();
  };

  const refreshFromPref = () => {
    const pref = readCcPref();
    applyCcState(section, pref);
    syncButtonUi(pref);
    return pref;
  };

  const purgeCaptionsHandle = () => {
    if (captionsHandle) {
      try { captionsHandle.destroy(); } catch {}
      captionsHandle = null;
      try { (section as any).__captionsHandle = null; } catch {}
    }
    if (onCCChangeListener) {
      document.removeEventListener("sc:cc-change", onCCChangeListener as EventListener, { capture: true } as any);
      onCCChangeListener = null;
    }
  };

  const markUnavailable = () => {
    section.removeAttribute("data-cc-available");
    removeCcButton();
    purgeCaptionsHandle();
    refreshFromPref();
  };

  const ensureButtonExists = () => {
    if (!section.classList.contains("hud-active")) return; // 🛑 Don't show button before HUD is visible

    let ccBtn = controls.querySelector<HTMLButtonElement>(".cc-button");
    if (!ccBtn) {
      const initialUiOn = readCcPref() ?? true;
      ccBtn = createCCButton(initialUiOn);
      ccBtn.classList.add("hud-btn", "cc-button");

      const stack = document.createElement("div");
      stack.className = "button-stack";
      stack.appendChild(ccBtn);
      controls.insertAdjacentElement("beforeend", stack);
    } else {
      if (!ccBtn.closest(".button-stack")) {
        const stack = document.createElement("div");
        stack.className = "button-stack";
        ccBtn.replaceWith(stack);
        stack.appendChild(ccBtn);
      }
      if (!ccBtn.classList.contains("hud-btn")) ccBtn.classList.add("hud-btn");

    }
  };

  const syncButtonUi = (on: boolean) => {
    const btn = controls.querySelector(".cc-button") as HTMLButtonElement | null;
    if (!btn) return;
    btn.setAttribute("aria-pressed", String(on));
    btn.classList.toggle("active", on);
    btn.setAttribute("aria-label", on ? "Closed captions (on)" : "Closed captions (off)");
    btn.title = on ? "Hide captions" : "Show captions";
  };

  const mountIfNeeded = () => {
    if (captionsHandle) return;

    captionsHandle = createCaptions();
    captionsHandle.mount(section);
    captionsHandle.attachToVideo(video);
    try { (section as any).__captionsHandle = captionsHandle; } catch {}

    refreshFromPref();

    const rawSrc = (video.currentSrc || video.src || "").split("?")[0];
    const srcUrl = new URL(rawSrc, window.location.href);
    const stem = (rawSrc.split("/").pop() || "").replace(/\.(mp4|webm|m4v)$/i, "");
    const pageOrigin = window.location.origin;
    const fallback = stem ? `${baseUrl}/videos/${stem}.srt` : "";

    let srtSameFolder = srcUrl.href.replace(/\.(mp4|webm|m4v)$/i, ".srt");
    if (srcUrl.origin !== pageOrigin) {
      srtSameFolder = fallback || srcUrl.pathname.replace(/\.(mp4|webm|m4v)$/i, ".srt");
    }

    const loadOnce = (target: string): Promise<boolean> => {
      try {
        const result = captionsHandle.loadIfExists(target) as unknown;
        if (result && typeof (result as any).then === "function") {
          return result as Promise<boolean>;
        }
        return Promise.resolve(!!result);
      } catch {
        return Promise.resolve(false);
      }
    };

    const loadSequential = (targets: string[]): Promise<boolean> => {
      if (!targets.length) return Promise.resolve(false);
      const [first, ...rest] = targets;
      return loadOnce(first)
        .then((ok) => {
          if (ok) return true;
          if (!rest.length) return false;
          return loadSequential(rest);
        })
        .catch(() => {
          if (!rest.length) return false;
          return loadSequential(rest);
        });
    };

    const probeList = [srtSameFolder];
    if (fallback && fallback !== srtSameFolder) probeList.push(fallback);
    const maybePromise = loadSequential(probeList);
    maybePromise
      .then((loaded) => {
        if (loaded) {
          section.setAttribute("data-cc-available", "1");

          // ⏱ Delay to avoid race with HUD fade out
          setTimeout(() => ensureButtonExists(), 300);

          refreshFromPref();
        } else {
          markUnavailable();
        }
      })
      .catch(() => {
        markUnavailable();
      });

    if (!onCCChangeListener) {
      onCCChangeListener = (ev: Event) => {
        const e = ev as CustomEvent<{ active: boolean; section: HTMLElement; video: HTMLVideoElement }>;
        if (!e?.detail) return;
        if (e.detail.section !== section) return;
        syncButtonUi(!!e.detail.active);
      };
      document.addEventListener("sc:cc-change", onCCChangeListener as EventListener, { capture: true });
    }
  };

  const unmountIfPresent = () => {
    purgeCaptionsHandle();
    const ccBtn = controls.querySelector(".cc-button") as HTMLButtonElement | null;
    if (ccBtn) {
      const stack = ccBtn.closest(".button-stack");
      stack ? stack.remove() : ccBtn.remove();
    }
    applyCcState(section, false);
    section.removeAttribute("data-cc-available");
  };

  return { mountIfNeeded, unmountIfPresent };
}
